import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'

import { Clock3, Pencil, Zap } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  dayKeyOf,
  delayWithinDay,
  nowSchedule,
  nowScheduleRounded,
  toClockTime,
  toDayKey,
  withClockTime,
  wouldClamp,
} from '@/utils/scheduleTime'

/**
 * Coarse on purpose. The input here is a judgment call, not a measurement —
 * a driver saying "35 minutes" is an estimate the user reinterprets from
 * experience ("that's Joe, so an hour"). Exact entry lives behind Edit.
 */
const DELAY_MINUTES = [15, 30, 45, 60] as const

function delayLabel(minutes: number): string {
  return minutes >= 60 ? `+${minutes / 60} hour` : `+${minutes} min`
}

type Props = {
  deliveryId: string
  /** Local ISO `YYYY-MM-DDTHH:MM`. */
  scheduledFor: string
  /** The page's shared clock — decides which menu this delivery gets. */
  now: Date
  /**
   * Computed by the tile (stage-aware: a truck on site is never late), so
   * the menu can never disagree with the stripe and the Late pill.
   */
  late: boolean
  onReschedule: (scheduledFor: string) => void
  /** The rail, rendered as the trigger. */
  children: ReactNode
}

function ReschedulePopover({
  deliveryId,
  scheduledFor,
  now,
  late,
  onReschedule,
  children,
}: Props) {
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(() => toClockTime(scheduledFor))

  // Each opening starts from the delivery's current time, so a stale draft
  // from a cancelled edit can't leak into the next one.
  useEffect(() => {
    if (open) {
      setEditing(false)
      setDraft(toClockTime(scheduledFor))
    }
  }, [open, scheduledFor])

  function apply(next: string) {
    onReschedule(next)
    setOpen(false)
  }

  const currentClock = toClockTime(scheduledFor)

  /**
   * Which menu this delivery gets. The same phone call means different
   * arithmetic depending on whether the slot has passed:
   *
   * - upcoming today  — "he'll be 30 late" counts from the *scheduled* time,
   *   plus Now for a truck arriving early.
   * - late            — the slot is gone, so delays count from *now* ("he'll
   *   be here in 30"), and the primary action is Just arrived. This is the
   *   one sanctioned crossing of the day rule: a late truck turning up
   *   is genuinely arriving today.
   * - a future day    — delays within its own day only; no Now, because that
   *   would silently drag the delivery across days. Anything bigger is a
   *   View / Edit job.
   */
  const futureDay = toDayKey(scheduledFor) > dayKeyOf(now)

  // Base the delay options count from. Rounded to 5 for the late case: those
  // are arrival estimates, and 11:00 reads as one where 11:02 does not. The
  // arrival button below is deliberately *not* rounded -- it records when the
  // truck actually turned up.
  const delayBase = late ? nowScheduleRounded(5, now) : scheduledFor

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      {/* Opens right: the rail sits at the tile's left edge, so a left-side
          surface would be clipped by the scroll container. */}
      <PopoverContent side="right" align="center" className="w-64">
        <p className="text-xs font-bold tracking-wide text-(--fg2) uppercase">
          Reschedule {deliveryId}
          <span className="ml-1 font-medium normal-case">
            {late ? '· was' : '· now'} {currentClock}
          </span>
        </p>

        {editing ? (
          <form
            className="flex flex-col gap-2.5"
            onSubmit={(event) => {
              event.preventDefault()
              if (draft) apply(withClockTime(scheduledFor, draft))
            }}
          >
            <Input
              type="time"
              /* The rule guards against surprise focus on page load. Here the
                 user just clicked Edit inside an open popover, and Radix has
                 already moved focus into the content — this only points it at
                 the field instead of the Save button. */
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              aria-label={`Exact time for ${deliveryId}`}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={!draft}>
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <>
            {/* No arrival shortcut on a future day's delivery — it would
                silently drag the row across days.

                The time is the exact minute, not a rounded one: this records
                when the truck turned up, and the user can still correct it via
                Edit. Rounding also used to push the time *forward* past the
                half-step — an arrival stamped in the future. */}
            {!futureDay ? (
              <Button
                variant="outline"
                className="justify-between"
                onClick={() => apply(nowSchedule(now))}
              >
                <span className="flex items-center gap-2">
                  <Zap aria-hidden="true" />
                  {late ? 'Just arrived' : 'Now'}
                </span>
                <span className="tabular-nums opacity-70">
                  {toClockTime(nowSchedule(now))}
                </span>
              </Button>
            ) : null}

            <div className="flex flex-col gap-1">
              <p className="text-[11px] font-bold tracking-wider text-(--fg2) uppercase">
                {late ? 'Expected in' : 'Delay'}
              </p>
              {DELAY_MINUTES.map((minutes) => {
                const next = delayWithinDay(delayBase, minutes)
                const clamped = wouldClamp(delayBase, minutes)
                return (
                  <Button
                    key={minutes}
                    variant="ghost"
                    className="justify-between"
                    /* A delay that only reaches 23:59 is not the delay the
                       user asked for — moving to another day is a
                       View / Edit job, so the option is simply unavailable. */
                    disabled={clamped}
                    onClick={() => apply(next)}
                  >
                    <span className="flex items-center gap-2">
                      <Clock3 aria-hidden="true" />
                      {late ? delayLabel(minutes).slice(1) : delayLabel(minutes)}
                    </span>
                    {/* The resulting time is the confirmation — no arithmetic
                        while a driver is talking at you. */}
                    <span className="tabular-nums opacity-70">
                      {clamped ? 'next day' : toClockTime(next)}
                    </span>
                  </Button>
                )
              })}
            </div>

            <Button
              variant="ghost"
              className="justify-start"
              onClick={() => setEditing(true)}
            >
              <Pencil aria-hidden="true" />
              Edit exact time…
            </Button>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}

export default ReschedulePopover
